import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL;

const KANBAN_COLUMNS = [
  { key: 'scheduled', label: 'Programada', color: 'border-amber-500', bg: 'bg-amber-500/10', badge: 'bg-amber-600' },
  { key: 'in_progress', label: 'En Progreso', color: 'border-blue-500', bg: 'bg-blue-500/10', badge: 'bg-blue-600' },
  { key: 'completed', label: 'Completada', color: 'border-green-500', bg: 'bg-green-500/10', badge: 'bg-green-600' },
  { key: 'invoiced', label: 'Facturada', color: 'border-indigo-500', bg: 'bg-indigo-500/10', badge: 'bg-indigo-600' },
];

function KanbanCard({ wo, onDragStart, onClick }) {
  const checklist = wo.checklist || [];
  const done = checklist.filter(c => c.completed).length;
  const pct = checklist.length > 0 ? Math.round((done / checklist.length) * 100) : 0;

  return (
    <div
      draggable
      onDragStart={(e) => { e.dataTransfer.setData('woId', wo.id); e.dataTransfer.effectAllowed = 'move'; onDragStart?.(wo.id); }}
      onClick={() => onClick?.(wo)}
      className="bg-slate-800 border border-slate-600 rounded-lg p-3 cursor-grab active:cursor-grabbing hover:border-slate-500 transition-all hover:shadow-lg hover:shadow-slate-900/50 group"
      data-testid={`kanban-card-${wo.id}`}
    >
      <div className="flex justify-between items-start mb-2">
        <span className="text-white font-semibold text-xs">{wo.work_order_number}</span>
        <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${wo.priority === 'high' ? 'bg-red-900/50 text-red-300' : wo.priority === 'medium' ? 'bg-amber-900/50 text-amber-300' : 'bg-slate-700 text-slate-400'}`}>
          {wo.priority || 'normal'}
        </span>
      </div>
      <p className="text-slate-300 text-xs truncate">{wo.client_name}</p>
      {wo.title && <p className="text-slate-500 text-[10px] truncate mt-0.5">{wo.title}</p>}

      <div className="mt-2 flex items-center justify-between">
        <div className="flex items-center gap-1">
          {wo.technician_names?.length > 0 ? (
            <span className="text-cyan-400 text-[10px] truncate max-w-[100px]">{wo.technician_names[0]}</span>
          ) : (
            <span className="text-red-400 text-[10px]">Sin asignar</span>
          )}
        </div>
        {checklist.length > 0 && (
          <div className="flex items-center gap-1">
            <div className="w-10 bg-slate-700 rounded-full h-1.5">
              <div className={`h-1.5 rounded-full transition-all ${pct === 100 ? 'bg-green-500' : pct > 50 ? 'bg-blue-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="text-slate-500 text-[9px]">{pct}%</span>
          </div>
        )}
      </div>

      {wo.scheduled_date && (
        <p className="text-slate-600 text-[10px] mt-1">{wo.scheduled_date}</p>
      )}
    </div>
  );
}

function KanbanColumn({ column, workOrders, onDrop, onCardClick }) {
  const [dragOver, setDragOver] = useState(false);
  const colWOs = workOrders.filter(wo => {
    if (column.key === 'invoiced') return wo.status === 'completed' && wo.auto_closed;
    if (column.key === 'completed') return wo.status === 'completed' && !wo.auto_closed;
    return wo.status === column.key;
  });

  return (
    <div
      className={`flex-1 min-w-[240px] rounded-xl border-t-4 ${column.color} ${dragOver ? 'ring-2 ring-offset-2 ring-offset-slate-900 ring-blue-500' : ''} transition-all`}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); const woId = e.dataTransfer.getData('woId'); if (woId) onDrop(woId, column.key); }}
      data-testid={`kanban-col-${column.key}`}
    >
      <div className={`${column.bg} rounded-t-lg px-3 py-2 flex items-center justify-between`}>
        <span className="text-white text-xs font-bold">{column.label}</span>
        <span className={`${column.badge} text-white text-[10px] px-2 py-0.5 rounded-full font-bold`}>{colWOs.length}</span>
      </div>
      <div className="p-2 space-y-2 min-h-[200px] max-h-[500px] overflow-y-auto bg-slate-900/40 rounded-b-lg">
        {colWOs.length === 0 ? (
          <div className="text-slate-600 text-xs text-center py-8 border-2 border-dashed border-slate-700 rounded-lg">
            Arrastra aqui
          </div>
        ) : (
          colWOs.map(wo => <KanbanCard key={wo.id} wo={wo} onClick={onCardClick} />)
        )}
      </div>
    </div>
  );
}

function WODetailModal({ wo, onClose, onTrigger, onUpdateChecklist, triggering }) {
  if (!wo) return null;
  const checklist = wo.checklist || [];
  const done = checklist.filter(c => c.completed).length;
  const pct = checklist.length > 0 ? Math.round((done / checklist.length) * 100) : 0;

  const flowSteps = [
    { label: 'Firmada', done: true },
    { label: 'Pagada', done: true },
    { label: 'Asignada', done: (wo.technician_names?.length || 0) > 0 },
    { label: 'Ingenieria', done: wo.engineering_notified },
    { label: 'En Progreso', done: wo.status === 'in_progress' || wo.status === 'completed' },
    { label: 'Completada', done: wo.status === 'completed' },
    { label: 'Facturada', done: wo.auto_closed },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-700 flex justify-between items-start">
          <div>
            <h2 className="text-white text-lg font-bold">{wo.work_order_number} — {wo.client_name}</h2>
            <p className="text-slate-400 text-xs mt-1">
              {wo.scheduled_date} | {wo.project_address || 'Sin direccion'} | Tecnico: {wo.technician_names?.join(', ') || 'Sin asignar'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="modal-trigger-btn" size="sm" onClick={() => onTrigger(wo.id)} disabled={triggering}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs">
              {triggering ? 'Ejecutando...' : 'Auto-Asignar + Ingenieria'}
            </Button>
            <Button size="sm" variant="outline" onClick={onClose}>X</Button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Flow */}
          <div className="flex items-center gap-1 flex-wrap">
            {flowSteps.map((step, i) => (
              <React.Fragment key={i}>
                <div className={`px-2 py-1 rounded text-[10px] font-semibold ${step.done ? 'bg-green-900/50 text-green-300' : 'bg-slate-700 text-slate-500'}`}>
                  {step.done ? '\u2713' : '\u25CB'} {step.label}
                </div>
                {i < flowSteps.length - 1 && <span className="text-slate-600 text-xs">\u2192</span>}
              </React.Fragment>
            ))}
          </div>

          {/* Progress */}
          {checklist.length > 0 && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <p className="text-slate-300 text-xs font-semibold uppercase">Checklist ({done}/{checklist.length})</p>
                <span className={`text-xs font-bold ${pct === 100 ? 'text-green-400' : 'text-amber-400'}`}>{pct}%</span>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-2 mb-3">
                <div className={`h-2 rounded-full transition-all ${pct === 100 ? 'bg-green-500' : pct > 50 ? 'bg-blue-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
              </div>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {checklist.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-slate-800 rounded p-2">
                    <input type="checkbox" checked={item.completed || false}
                      onChange={e => onUpdateChecklist(wo.id, idx, e.target.checked)}
                      className="rounded border-slate-600" />
                    <span className={`text-xs flex-1 ${item.completed ? 'text-green-400 line-through' : 'text-white'}`}>
                      {item.description || item.task || `Item ${idx + 1}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {wo.notes && (
            <div className="bg-slate-800/60 rounded p-3">
              <p className="text-slate-400 text-xs font-semibold mb-1">Notas</p>
              <p className="text-slate-300 text-xs">{wo.notes}</p>
            </div>
          )}

          {wo.description && (
            <div className="bg-slate-800/60 rounded p-3">
              <p className="text-slate-400 text-xs font-semibold mb-1">Descripcion</p>
              <p className="text-slate-300 text-xs">{wo.description}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PostSalePage() {
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWO, setSelectedWO] = useState(null);
  const [msg, setMsg] = useState('');
  const [triggering, setTriggering] = useState(false);
  const [activeTab, setActiveTab] = useState('kanban');

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/work-orders`, { credentials: 'include' });
      if (res.ok) setWorkOrders(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  const showMsg = (t) => { setMsg(t); setTimeout(() => setMsg(''), 5000); };

  const handleDrop = async (woId, newStatus) => {
    const actualStatus = newStatus === 'invoiced' ? 'completed' : newStatus;
    const extra = newStatus === 'invoiced' ? { auto_closed: true } : {};
    try {
      const res = await fetch(`${API}/api/work-orders/${woId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: actualStatus, ...extra })
      });
      if (res.ok) {
        showMsg(`OT movida a ${KANBAN_COLUMNS.find(c => c.key === newStatus)?.label}`);
        fetchData();
      } else { showMsg('Error al mover OT'); }
    } catch (e) { showMsg('Error de conexion'); }
  };

  const handleTrigger = async (woId) => {
    setTriggering(true);
    try {
      const res = await fetch(`${API}/api/post-sale/trigger/${woId}`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        showMsg(`Tecnico: ${data.auto_assigned?.join(', ') || 'N/A'} | Ingenieria: ${data.engineering_notified ? 'Si' : 'No'}`);
        fetchData();
      }
    } catch (e) { showMsg('Error'); }
    setTriggering(false);
  };

  const handleUpdateChecklist = async (woId, idx, completed) => {
    const wo = workOrders.find(w => w.id === woId);
    if (!wo) return;
    const updated = [...(wo.checklist || [])];
    if (updated[idx]) {
      updated[idx].completed = completed;
      updated[idx].completed_at = completed ? new Date().toISOString() : null;
    }
    await fetch(`${API}/api/work-orders/${woId}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ checklist: updated })
    });
    fetchData();
    if (selectedWO?.id === woId) setSelectedWO({ ...selectedWO, checklist: updated });
  };

  // Stats
  const stats = {
    total: workOrders.length,
    scheduled: workOrders.filter(w => w.status === 'scheduled').length,
    in_progress: workOrders.filter(w => w.status === 'in_progress').length,
    completed: workOrders.filter(w => w.status === 'completed').length,
    unassigned: workOrders.filter(w => !w.assigned_technicians?.length).length,
  };

  return (
    <div data-testid="post-sale-page" className="space-y-5">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Post-Venta Automatizado</h1>
          <p className="text-slate-400 text-sm">Pipeline Kanban: arrastra OTs entre columnas | Asignacion + Ingenieria + Cierre automatico</p>
        </div>
      </div>
      {msg && <div className="bg-blue-900/30 border border-blue-700 text-blue-200 px-4 py-2 rounded text-sm">{msg}</div>}

      {/* KPI Row */}
      <div className="grid grid-cols-5 gap-3">
        {[
          { val: stats.total, label: 'Total', text: 'text-white', bg: 'bg-slate-800 border-slate-700' },
          { val: stats.scheduled, label: 'Programadas', text: 'text-amber-300', bg: 'bg-amber-950/30 border-amber-800' },
          { val: stats.in_progress, label: 'En Progreso', text: 'text-blue-300', bg: 'bg-blue-950/30 border-blue-800' },
          { val: stats.completed, label: 'Completadas', text: 'text-green-300', bg: 'bg-green-950/30 border-green-800' },
          { val: stats.unassigned, label: 'Sin Asignar', text: 'text-red-300', bg: 'bg-red-950/30 border-red-800' },
        ].map((k, i) => (
          <div key={i} className={`${k.bg} border rounded-lg p-3 text-center`}>
            <p className={`text-2xl font-extrabold ${k.text}`}>{k.val}</p>
            <p className="text-slate-500 text-[10px] uppercase">{k.label}</p>
          </div>
        ))}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="kanban" data-testid="tab-kanban">Kanban</TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-list">Lista</TabsTrigger>
        </TabsList>

        {/* KANBAN VIEW */}
        <TabsContent value="kanban" className="mt-4">
          <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
            {KANBAN_COLUMNS.map(col => (
              <KanbanColumn key={col.key} column={col} workOrders={workOrders}
                onDrop={handleDrop} onCardClick={setSelectedWO} />
            ))}
          </div>
          <p className="text-slate-600 text-xs text-center mt-2">Arrastra las tarjetas entre columnas para cambiar el estado</p>
        </TabsContent>

        {/* LIST VIEW */}
        <TabsContent value="list" className="mt-4">
          <div className="space-y-2">
            {workOrders.length === 0 ? (
              <p className="text-slate-500 text-center py-8">Sin ordenes de trabajo</p>
            ) : workOrders.map(wo => {
              const checklist = wo.checklist || [];
              const done = checklist.filter(c => c.completed).length;
              const pct = checklist.length > 0 ? Math.round((done / checklist.length) * 100) : 0;
              const sc = { scheduled: 'bg-amber-600', in_progress: 'bg-blue-600', completed: 'bg-green-600' };
              const sl = { scheduled: 'Programada', in_progress: 'En Progreso', completed: 'Completada' };
              return (
                <div key={wo.id} className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 flex items-center gap-4 cursor-pointer hover:bg-slate-700/60"
                  onClick={() => setSelectedWO(wo)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-semibold text-sm">{wo.work_order_number}</span>
                      <span className={`${sc[wo.status] || 'bg-slate-600'} text-white text-[10px] px-2 py-0.5 rounded`}>{sl[wo.status] || wo.status}</span>
                    </div>
                    <p className="text-slate-400 text-xs">{wo.client_name} | {wo.scheduled_date || 'Sin fecha'}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-cyan-400 text-xs">{wo.technician_names?.join(', ') || 'Sin asignar'}</p>
                    {checklist.length > 0 && (
                      <div className="flex items-center gap-1 justify-end">
                        <div className="w-16 bg-slate-700 rounded-full h-1.5">
                          <div className={`h-1.5 rounded-full ${pct === 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-slate-500 text-[10px]">{pct}%</span>
                      </div>
                    )}
                  </div>
                  <Button size="sm" variant="outline" className="text-xs shrink-0" onClick={e => { e.stopPropagation(); handleTrigger(wo.id); }}>
                    Auto
                  </Button>
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Detail Modal */}
      {selectedWO && (
        <WODetailModal wo={selectedWO} onClose={() => setSelectedWO(null)}
          onTrigger={handleTrigger} onUpdateChecklist={handleUpdateChecklist} triggering={triggering} />
      )}
    </div>
  );
}
